import { Box, IconButton, TextField, Typography } from "@mui/material";
import { RichTreeView } from "@mui/x-tree-view/RichTreeView";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  setCIBottlerData,
  setCIFilterApplied,
  setCIFinalTreeData,
  setCISelectedTreeIds,
  setCITransformedTreeData,
} from "../../../redux/CIEntityTree";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import ApiService from "../../../utils/fetch";
import ButtonComponent from "../../../Components/Common/button/Button";

// Helper function to find the parent node of a given item ID
function getParentNode(items, id) {
  for (const item of items) {
    if (item.children) {
      if (item.children.some((child) => child.id === id)) {
        return item;
      } else {
        const parentNode = getParentNode(item.children, id);
        if (parentNode) {
          return parentNode;
        }
      }
    }
  }
  return undefined;
}

// Helper function to get all parent IDs of a given item ID
function getAllParentIds(items, id) {
  const parentIds = [];
  let parent = getParentNode(items, id);
  while (parent) {
    parentIds.push(parent.id);
    parent = getParentNode(items, parent.id);
  }
  return parentIds;
}

// Helper function to get selected IDs along with their children
function getSelectedIdsAndChildrenIds(items, selectedIds) {
  const selectedIdIncludingChildrenIds = new Set([...selectedIds]);
  for (const item of items) {
    if (selectedIds.includes(item.id)) {
      selectedIdIncludingChildrenIds.add(item.id);
      if (item.children) {
        const childrenIds = item.children.map((child) => child.id);
        const childrenSelectedIds = getSelectedIdsAndChildrenIds(
          item.children,
          childrenIds,
        );
        childrenSelectedIds.forEach((selectedId) =>
          selectedIdIncludingChildrenIds.add(selectedId),
        );
      }
    } else if (item.children) {
      const childrenSelectedIds = getSelectedIdsAndChildrenIds(
        item.children,
        selectedIds,
      );
      childrenSelectedIds.forEach((selectedId) =>
        selectedIdIncludingChildrenIds.add(selectedId),
      );
    }
  }
  return [...Array.from(selectedIdIncludingChildrenIds)];
}

//Function to determine which IDs should be set based on the current and new selections
function determineIdsToSet(fullTreeData, newIds, currentIds) {
  const isDeselectingNode = currentIds.length > newIds.length;

  if (isDeselectingNode) {
    const removed = currentIds.filter((id) => !newIds.includes(id))[0];
    const parentIdsToRemove = getAllParentIds(fullTreeData, removed);
    const childIdsToRemove = getSelectedIdsAndChildrenIds(fullTreeData, [
      removed,
    ]);

    return newIds.filter(
      (id) => !parentIdsToRemove.includes(id) && !childIdsToRemove.includes(id),
    );
  }

  const idsToSet = getSelectedIdsAndChildrenIds(fullTreeData, newIds);
  let parent = getParentNode(fullTreeData, newIds[newIds.length - 1]);

  while (parent) {
    const childIds = parent.children?.map((node) => node.id) ?? [];
    const allChildrenSelected = childIds.every((id) => idsToSet.includes(id));

    if (allChildrenSelected) {
      if (!idsToSet.includes(parent.id)) {
        idsToSet.push(parent.id);
      }
      parent = getParentNode(fullTreeData, parent.id);
    } else {
      break;
    }
  }
  return idsToSet;
}

const CIEntitySelector = ({ popupHandler }) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedIds, setSelectedIds] = useState([]);
  const [treeData, setTreeData] = useState([]);

  const dispatch = useDispatch();

  const filterApplied = useSelector((state) => state.CIentity.CIfilterApplied);
  const transformedTreeDataRedux = useSelector(
    (state) => state.CIentity.CItransformedTreeData,
  );
  const email = useSelector((state) => state.user.email);
  const selectedTreeIds = useSelector(
    (state) => state.CIentity.CIselectedTreeIds,
  );

  const transformData = (data) => {
    return data.map((item, bottlerIndex) => {
      const displayLabel = item.bottlerOwnerCode
        ? `${item.bottler} - ${item.bottlerOwnerCode}${
            item.bottlerOwnerDescription
              ? ` - ${item.bottlerOwnerDescription}`
              : ""
          }`
        : item.bottler;

      return {
        id: `bottler-${bottlerIndex + 1}`,
        label: displayLabel,
        bottler: item.bottler,
        bottlerOwnerCode: item.bottlerOwnerCode,
        bottlerOwnerDescription: item.bottlerOwnerDescription, // optional but safe
        children: (item.profitCenterDescription || []).map(
          (pcDesc, countryIndex) => ({
            id: `profitCenter-${bottlerIndex + 1}-${countryIndex + 1}`,
            label: pcDesc,
            profitCenterDescription: pcDesc,
            bottler: item.bottler,
            bottlerOwnerCode: item.bottlerOwnerCode,
            bottlerOwnerDescription: item.bottlerOwnerDescription,
          }),
        ),
      };
    });
  };
  
  useEffect(() => {
    if (
      transformedTreeDataRedux === null ||
      transformedTreeDataRedux.length === 0
    ) {
      const postData = async () => {
        try {
          const response = await ApiService.get("/v1/ci/bottler-selection");
          const bottler = response.data[0]?.bottler;
          dispatch(setCIBottlerData(bottler));

          const transformedData = transformData(response.data || []);
          setTreeData(transformedData);

          const allIds = getAllIds(transformedData);

          dispatch(setCITransformedTreeData(transformedData));
          dispatch(setCISelectedTreeIds(allIds));

          const filteredItems = allIds.filter((item) =>
            item.startsWith("profitCenter"),
          );

          const ApplyTreeViewData = filterBottlersByCountry(
            filteredItems,
            transformedData,
          );

          dispatch(setCIFinalTreeData(ApplyTreeViewData));
        } catch (error) {
          console.error("Error posting data", error);
        }
      };
      postData();
    } else {
      setTreeData(transformedTreeDataRedux);
      if (filterApplied) {
        setSelectedIds(selectedTreeIds);
      }
    }
  }, [email]);

  //get all Ids
  const getAllIds = (items) => {
    const ids = [];
    items.forEach((item) => {
      ids.push(item.id);
      if (item.children) {
        ids.push(...getAllIds(item.children));
      }
    });
    return ids;
  };

  const filterTreeData = (items, query) => {
    if (!query) {
      return items;
    }
    return items
      .filter((item) => {
        const matches = item.label.toLowerCase().includes(query.toLowerCase());
        const children = item.children
          ? filterTreeData(item.children, query)
          : [];
        return matches || children.length > 0;
      })
      .map((item) => {
        const matches = item.label.toLowerCase().includes(query.toLowerCase());
        const children = matches
          ? item.children
          : item.children
            ? filterTreeData(item.children, query)
            : [];
        return {
          ...item,
          children: children,
        };
      });
  };

  const filteredData = filterTreeData(treeData, searchQuery);

  const handleSearchChange = (event) => {
    setSearchQuery(event.target.value);
  };

  //Event handler for when selected items change
  const handleSelectedItemsChange = (_event, ids) => {
    const newSelectedIds = determineIdsToSet(treeData, ids, selectedIds);
    setSelectedIds(newSelectedIds);
  };

  const filterBottlersByCountry = (profitCenterIds, transformedData) => {
    const groupedData = {};

    (transformedData ?? []).forEach((bottler) => {
      (bottler.children ?? [])
        .filter((country) => profitCenterIds.includes(country.id))
        .forEach((country) => {
          const ownerCode = country.bottlerOwnerCode || "";
          const countryName = country.profitCenterDescription || "";

          if (!groupedData[ownerCode]) {
            groupedData[ownerCode] = [];
          }

          if (!groupedData[ownerCode].includes(countryName)) {
            groupedData[ownerCode].push(countryName);
          }
        });
    });

    return Object.entries(groupedData).map(
      ([bottlerOwnerCode, countryList]) => ({
        bottlerOwnerCode,
        countryList,
      }),
    );
  };

  const applySelection = (selectedData) => {
    const filteredItems = selectedData.filter((item) =>
      item.startsWith("profitCenter"),
    );

    const ApplyTreeViewData = filterBottlersByCountry(filteredItems, treeData);
    console.log(ApplyTreeViewData, "ApplyTreeViewData Apply");

    dispatch(setCIFinalTreeData(ApplyTreeViewData));
    dispatch(setCISelectedTreeIds(selectedData));
    dispatch(setCIFilterApplied(true));
  };

  // apply and close toggle
  const applyAndClose = () => {
    applySelection(selectedIds);
    popupHandler(false);
  };

  // cancel and close toggle
  const cancelAndClose = () => {
    popupHandler(false);
  };

  return (
    <Box
      sx={{
        width: 340,
        display: "flex",
        flexDirection: "column",
        height: "100%",
      }}
      role="presentation"
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          p: 2,
          borderBottom: "1px solid #ccc",
        }}
      >
        <Typography variant="h6">Bottler Selection</Typography>
        <IconButton
          onClick={cancelAndClose}
          sx={{
            color: "grey",
          }}
        >
          <CloseIcon />
        </IconButton>
      </Box>
      <Box sx={{ flexGrow: 1, p: 2, overflow: "auto" }}>
        <TextField
          fullWidth
          variant="outlined"
          size="small"
          placeholder="Search"
          margin="normal"
          value={searchQuery}
          onChange={handleSearchChange}
          InputProps={{
            endAdornment: (
              <IconButton>
                <SearchIcon />
              </IconButton>
            ),
          }}
        />
        <RichTreeView
          multiSelect
          checkboxSelection
          selectedItems={selectedIds}
          onSelectedItemsChange={handleSelectedItemsChange}
          items={filteredData}
          sx={{
            flexGrow: 1,
            "& .MuiTreeItem-label": {
              fontSize: "11px",
            },
          }}
        />
      </Box>
      <Box
        sx={{
          p: 2,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          borderTop: "1px solid #ccc",
        }}
      >
        <ButtonComponent
          onClick={applyAndClose}
          maxWidth="150px"
          label="Apply"
          bgColor="black"
          color="white"
        />
      </Box>
    </Box>
  );
};

export default CIEntitySelector;
